import React from 'react';

import { observer } from 'mobx-react';

import { Tooltip } from '@mui/material';

import InfoIcon from '@mui/icons-material/Info';

import { Button } from 'components/Button';

import { useStore } from 'utils/hooks/store';

import CustomModal from 'components/CustomModal';

import { StyledTextField } from 'components/Input/styles';

import { modifyInputValue } from 'containers/FieldManager/components/CreateEditFieldModal/types';

import { colors } from 'theme/colors';

import { FieldLabel, FieldLabelRow } from './styles';

const AddModalForCreatingStage = ({ open }: { open: boolean }): React.ReactElement<{ open: boolean }> => {
  const { pipelineDetails } = useStore();
  const prefix = pipelineDetails.data?.key ? `${pipelineDetails.data?.key.replace(/\s+/g, '')}__` : '';

  const defaultStageData = {
    name: '',
    key: '',
  };

  const [stageData, setStageData] = React.useState(defaultStageData);
  const [nameError, setNameError] = React.useState('');

  const handleSubmit = (): void => {
    if (!stageData.name) {
      return;
    }

    if (stageData.name.length > 255) {
      setNameError('Stage name must be 255 characters or less');
      return;
    }

    const success = pipelineDetails.addStage({ ...stageData, key: `${prefix}${stageData.key}` });
    if (!success) {
      return;
    }
    pipelineDetails.setIsModalOpenForCreatingStage(false);
    setStageData(defaultStageData);
    setNameError('');
  };

  const handleClose = (): void => {
    pipelineDetails.setIsModalOpenForCreatingStage(false);
    setStageData(defaultStageData);
    setNameError('');
  };

  const handleNameChange = (value: string): void => {
    const keyVal = modifyInputValue(value, prefix.length);
    setStageData({ name: value, key: keyVal });

    if (value.length > 255) {
      setNameError('Stage name must be 255 characters or less');
    } else {
      setNameError('');
    }
  };

  return (
    <CustomModal
      openModal={open}
      modalTitle="Add Stage"
      onClose={handleClose}
      cancelButton={
        <Button size="large" variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      }
      submitButton={
        <Button size="large" variant="contained" onClick={handleSubmit} disabled={!stageData.name}>
          Add
        </Button>
      }
    >
      <FieldLabelRow>
        <FieldLabel>Stage name</FieldLabel>
      </FieldLabelRow>
      <StyledTextField
        value={stageData.name}
        placeholder="Stage Name"
        onChange={(e) => handleNameChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
          }
        }}
        fullWidth
        error={!!nameError}
        helperText={nameError}
      />
      <FieldLabelRow>
        <FieldLabel>API name</FieldLabel>
        <Tooltip
          componentsProps={{
            tooltip: {
              sx: {
                color: colors.common.white,
                padding: '0px 8px',
                fontSize: '14px',
                borderRadius: '4px',
                fontWeight: 400,
              },
            },
          }}
          title="Used to read from and write to the field from the API"
          placement="right"
        >
          <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Tooltip>
      </FieldLabelRow>
      <StyledTextField
        fullWidth
        id="key"
        name="key"
        value={stageData.key.replace(prefix, '')}
        placeholder="API name"
        disabled={!stageData.name}
        onChange={(e) => setStageData({ ...stageData, key: e.target.value })}
        InputProps={{
          startAdornment: <p style={{ color: colors.grey[600], margin: 0, whiteSpace: 'nowrap' }}>{prefix}</p>,
        }}
      />
    </CustomModal>
  );
};

export default observer(AddModalForCreatingStage);
